import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type { UpsertEstimationItemResourceRequest } from '../../schema/data-contracts';
import type { MutationProps } from '../types';

const getResourcesKey = 'estimation-item-resources';

export const useEstimationItemResourceQueryActions = () => {
  const { auth } = useAuth();
  const {
    estimationItemResourceController: {
      v1EstimationsResourcesList,
      v1EstimationItemsResourcesList,
      v1EstimationItemsResourcesUpdate,
    },
  } = useApi();

  const queryClient = useQueryClient();

  const useGetResourcesByEstimationItemId = (estimationItemId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && !!estimationItemId,
      queryKey: [getResourcesKey, 'estimationItemId', estimationItemId],
      queryFn: async () => {
        const { data } = await v1EstimationItemsResourcesList(estimationItemId!);
        return data;
      },
    });

  const useGetResourcesByEstimationId = (estimationId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && Boolean(estimationId),
      queryKey: [getResourcesKey, 'estimationId', estimationId],
      queryFn: async () => {
        const { data } = await v1EstimationsResourcesList(estimationId!);
        return data;
      },
    });

  const useUpdateResourcesOfEstimationItem = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-item-resources-update'],
      mutationFn: async ({
        estimationItemId,
        resources,
      }: {
        estimationItemId: string;
        resources: UpsertEstimationItemResourceRequest[];
      }) => {
        const { data } = await v1EstimationItemsResourcesUpdate(estimationItemId, resources);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [getResourcesKey],
        });
      },
    });

  return {
    useGetResourcesByEstimationItemId,
    useGetResourcesByEstimationId,
    useUpdateResourcesOfEstimationItem,
  };
};
